import { isFreeEmailDomain } from '@repo/lib';

/**
 * Check if an email domain is a generic (non-business) domain.
 *
 * @param {string} emailOrDomain - Either a full email address or just the domain
 * @returns {boolean} true if the domain is a generic email provider
 */
export function isGenericEmailDomain(emailOrDomain) {
  return isFreeEmailDomain(emailOrDomain);
}

/**
 * Extract the domain from an email address.
 *
 * @param {string} email - The email address
 * @returns {string|null} The domain part, or null if invalid
 *
 * @example
 * extractDomainFromEmail('john@acme.com') // 'acme.com'
 * extractDomainFromEmail('invalid')        // null
 */
export function extractDomainFromEmail(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    return null;
  }

  const domain = email.split('@')[1];
  return domain && domain.includes('.') ? domain.toLowerCase() : null;
}

/**
 * Get website URL from email domain.
 * For custom business emails, the domain is likely their website.
 *
 * @param {string} email - The email address
 * @returns {string|null} The website URL (https://domain), or null if generic email
 *
 * @example
 * getWebsiteFromEmail('john@acme.com')   // 'https://acme.com'
 * getWebsiteFromEmail('john@gmail.com')  // null (generic email)
 */
export function getWebsiteFromEmail(email) {
  const domain = extractDomainFromEmail(email);

  if (!domain || isGenericEmailDomain(domain)) {
    return null;
  }

  return `https://${domain}`;
}
